import React, { useState, useRef, useEffect } from "react";
import { useLanguage } from '../contexts/LanguageContext';
import { useTextSize } from '../contexts/TextSizeContext';

export default function Header({ user, onLogout, onRefresh, isRefreshing, onResetChat, showResetChat }) {
  const { currentLanguage, setLanguage, t } = useLanguage();
  const { textSize, setTextSize, currentTextSize, textSizeConfig } = useTextSize();
  const [showTextSizeMenu, setShowTextSizeMenu] = useState(false);
  const textSizeMenuRef = useRef(null);

  const toggleLanguage = () => {
    const newLang = currentLanguage === 'en' ? 'de' : 'en';
    setLanguage(newLang);
  };

  const handleTextSizeChange = (size) => {
    setTextSize(size);
    setShowTextSizeMenu(false);
  };

  // Close text size menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (textSizeMenuRef.current && !textSizeMenuRef.current.contains(event.target)) {
        setShowTextSizeMenu(false);
      }
    };

    if (showTextSizeMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTextSizeMenu]);

  return (
    <div>
      {/* Header */}
      <div className="bg-blue-600 w-full py-2">
        <div className="flex justify-between items-center px-4">
          <div className="flex items-center">
            <img
              src="assets/donexus-logo-white.png"
              alt="DoNexus"
              className="h-5 w-auto"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "block";
              }}
            />
            <div className="hidden text-white font-bold text-lg">DoNexus</div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Refresh Button */}
            {onRefresh && (
              <button
                onClick={onRefresh}
                disabled={isRefreshing}
                className="text-white hover:bg-white/20 p-2 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={t('header.refresh') || 'Refresh'}
              >
                <svg 
                  className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                  />
                </svg>
              </button>
            )}
            
            {/* Reset Chat Button */}
            {onResetChat && showResetChat && (
              <button
                onClick={onResetChat}
                className="text-white hover:bg-white/20 p-2 rounded transition-colors"
                title={t('aiAssistant.resetChat')}
              >
                <svg 
                  className="w-4 h-4" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                  />
                </svg>
              </button>
            )}
            
            {/* Language Toggle Button */}
            <button
              onClick={toggleLanguage}
              className="text-white hover:bg-white/20 p-2 rounded transition-colors text-sm font-medium"
              title={currentLanguage === 'en' ? t('header.switchToGerman') : t('header.switchToEnglish')}
            >
              {currentLanguage === 'en' ? 'DE' : 'EN'}
            </button>
            
            {/* Text Size Selector */}
            <div className="relative" ref={textSizeMenuRef}>
              <button
                onClick={() => setShowTextSizeMenu(!showTextSizeMenu)}
                className="text-white hover:bg-white/20 p-2 rounded transition-colors flex items-center justify-center"
                title={t('header.textSize') || 'Text Size'}
              >
                <span className="text-base font-bold">T</span>
              </button>
              
              {/* Dropdown Menu */}
              {showTextSizeMenu && (
                <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-50 min-w-[140px]">
                  {Object.entries(textSizeConfig).map(([size, config]) => (
                    <button
                      key={size}
                      onClick={() => handleTextSizeChange(size)}
                      className={`w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center justify-between ${
                        textSize === size ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                      }`}
                    >
                      <span className="text-sm">
                        {size === 'extra-small' && (t('aiAssistant.textSizeExtraSmall') || 'Extra Small')}
                        {size === 'small' && (t('aiAssistant.textSizeSmall') || 'Small')}
                        {size === 'medium' && (t('aiAssistant.textSizeMedium') || 'Medium')}
                        {size === 'large' && (t('aiAssistant.textSizeLarge') || 'Large')}
                        {size === 'extra-large' && (t('aiAssistant.textSizeExtraLarge') || 'Extra Large')}
                      </span>
                      <span className="font-bold ml-2">{config.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {user && (
              <button
                onClick={onLogout}
                className="text-white hover:bg-white/20 p-2 rounded transition-colors"
                title={t('header.logout')}
              >
                <svg 
                  className="w-4 h-4" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" 
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
